import { ReactNode } from 'react';
import clsx from 'clsx';
import { FIELD_LABELS } from '@/lib/i18n/deviceFields';

export function KvCard({
  title,
  icon,
  children,
  action,
}: {
  title: string;
  icon?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}) {
  // `h-full` + `flex flex-col` propagate the grid row's stretched height
  // down into the card body, so cards in the same dashboard row align to
  // the same bottom edge. The body uses `flex-1` to absorb whatever slack
  // remains after the header, which lets footer-style children (e.g. the
  // "打开性能面板" link with `mt-auto`) sink to the bottom of the card
  // instead of clinging to the kv list above them.
  return (
    <section className="card-pad flex h-full flex-col">
      <header className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-ink">
          {icon}
          <span>{title}</span>
        </div>
        {action}
      </header>
      <div className="flex flex-1 flex-col">{children}</div>
    </section>
  );
}

/**
 * Decide whether a value is short enough to share a row with another short
 * sibling. CJK chars are wider so we count them as 1.5 to be slightly
 * conservative; comma / newline / heavy punctuation forces a full row.
 */
function isCompact(v: ReactNode): boolean {
  if (typeof v !== 'string') return false;
  if (v.length === 0) return true;
  if (/[\n,，]/.test(v)) return false;
  let weight = 0;
  for (const ch of v) {
    weight += /[\u4e00-\u9fff\uff00-\uffef]/.test(ch) ? 1.5 : 1;
  }
  return weight <= 24;
}

/** Threshold above which a string value gets collapsed into a <details>. */
const LONG_STRING_THRESHOLD = 200;

/**
 * Adaptive N-column grid. Each entry takes a single column when its value
 * is short (typical for numeric / single-token fields like `level: 87` or
 * `density: 420`); long values such as activity names or fingerprints
 * automatically span the full row width.
 *
 * `columns` lets the parent dashboard expand cards that span 2-3 grid
 * cells horizontally to also use 3 / 4 internal sub-columns instead of 2,
 * so the extra horizontal space actually gets used by the kv pairs rather
 * than wasted as whitespace next to half-row entries. Defaults to 2.
 */
export function KvList({
  entries,
  columns = 2,
}: {
  entries: Array<[string, ReactNode]>;
  /** Internal column count: 2 (default), 3, or 4 — caller-driven to match outer span. */
  columns?: 2 | 3 | 4;
}) {
  // Map runtime column count → static Tailwind classes (Tailwind can't
  // generate `grid-cols-${n}` from a dynamic expression because it scans
  // source files at build time).
  const gridCls =
    columns === 4
      ? 'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4'
      : columns === 3
        ? 'grid grid-cols-2 md:grid-cols-3'
        : 'grid grid-cols-2';
  // Tailwind's `col-span-N` for the long-value spillover row uses static
  // classes too — pick the right one for the current column count.
  const fullSpanCls =
    columns === 4
      ? 'col-span-2 md:col-span-3 xl:col-span-4'
      : columns === 3
        ? 'col-span-2 md:col-span-3'
        : 'col-span-2';
  return (
    <dl className={clsx(gridCls, 'gap-x-3 gap-y-1.5')}>
      {entries.map(([k, v], i) => {
        const label = FIELD_LABELS[k];
        const compact = isCompact(v);
        // Very long string values (≥ 200 chars) like Wi-Fi `rawInfoLine`
        // would otherwise tower over neighbouring cards in the same grid
        // row. Collapse them into a <details> so they only consume one
        // line until clicked. Export / Commands page still see the full
        // text via the underlying data; this is purely a render-time fold.
        const isLong = typeof v === 'string' && v.length >= LONG_STRING_THRESHOLD;
        return (
          <div
            key={i}
            className={clsx(
              'min-w-0 border-b border-dashed border-line/70 pb-1.5 last:border-0 last:pb-0',
              (!compact || isLong) && fullSpanCls
            )}
          >
            <dt className="flex items-baseline gap-1.5 text-[11px] leading-tight text-ink-muted">
              <span className="font-mono">{k}</span>
              {label && <span className="text-ink-dim">· {label}</span>}
            </dt>
            <dd className="break-words whitespace-pre-wrap text-sm font-medium leading-snug text-ink">
              {isLong ? (
                <details className="group">
                  <summary className="cursor-pointer text-xs text-accent marker:hidden [&::-webkit-details-marker]:hidden">
                    点击展开（{(v as string).length} 字符）
                  </summary>
                  <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded bg-bg-muted px-2 py-1 font-mono text-[11px] text-ink-muted">
                    {v as string}
                  </pre>
                </details>
              ) : (
                v ?? <span className="text-ink-dim">-</span>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
